import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Settings.module.scss";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import ProductsService from "../../api/services/products.service";
import Button from "../../components/common/button/Button";
import Modal from "../../components/common/modal/Modal";
import Select from "../../components/common/select/Select";
import { setCookie } from "../../utils/cookies";

const Settings = () => {
  const { t, i18n } = useTranslation("translation");
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState(
    i18n.language || "en",
  );

  useEffect(() => {
    setSelectedLanguage(i18n.language || "en");
  }, [i18n.language]);

  const handleLanguageChange = (value: { id: string; name: string } | null) => {
    if (value) {
      const langCode = value.id;
      setSelectedLanguage(langCode);
      setCookie("lux_admin_lang", langCode, 365);
      i18n.changeLanguage(langCode);
    }
  };

  const handleSyncClick = () => {
    setShowWarningModal(true);
  };

  const handleConfirmSync = async () => {
    setShowWarningModal(false);
    setSyncing(true);
    setMessage(null);

    try {
      const response = await ProductsService.syncElasticsearch();
      setMessage({
        type: "success",
        text:
          response.data.message ||
          "Product sync started successfully. This may take a few minutes.",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        (t("settings.errors.failed_to_start_sync") as string);
      setMessage({
        type: "error",
        text: errorMessage,
      });
    } finally {
      setSyncing(false);
      // Clear message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    }
  };

  return (
    <div className={styles.container}>
      <TitleUpdater title={t("settings.title") as string} />
      <div className={styles.header}>
        <h1 className={styles.title}>{t("settings.title") as string}</h1>
        <p className={styles.subtitle}>{t("settings.subtitle") as string}</p>
      </div>

      <div className={styles.section}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div>
              <h2 className={styles.cardTitle}>
                {t("settings.language.title") as string}
              </h2>
              <p className={styles.cardDescription}>
                {t("settings.language.description") as string}
              </p>
            </div>
          </div>
          <div className={styles.cardBody}>
            <Select
              value={selectedLanguage}
              options={[
                { id: "en", name: t("languages.en") as string },
                { id: "az", name: t("languages.az") as string },
                { id: "ru", name: t("languages.ru") as string },
              ]}
              label={t("settings.language.preferred_language") as string}
              onChange={handleLanguageChange}
              className={styles.languageSelect}
            />
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div>
              <h2 className={styles.cardTitle}>
                {t("settings.elasticsearch.title") as string}
              </h2>
              <p className={styles.cardDescription}>
                {t("settings.elasticsearch.description") as string}
              </p>
            </div>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.warningBox}>
              <svg
                className={styles.warningIcon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div className={styles.warningContent}>
                <strong>{t("settings.elasticsearch.caution") as string}</strong>
                <ul>
                  {Object.values(
                    t("settings.elasticsearch.warning_items", {
                      returnObjects: true,
                    }) as Record<string, string>,
                  ).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>

            {message && (
              <div
                className={`${styles.alert} ${
                  message.type === "success"
                    ? styles.alertSuccess
                    : styles.alertError
                }`}
              >
                <svg
                  className={styles.alertIcon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {message.type === "success" ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  )}
                </svg>
                {message.text}
              </div>
            )}

            <div className={styles.actionButton}>
              <Button
                onClick={handleSyncClick}
                disabled={syncing}
                className={styles.syncButton}
              >
                {syncing
                  ? (t("settings.elasticsearch.syncing") as string)
                  : (t("settings.elasticsearch.sync_button") as string)}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        title={t("settings.elasticsearch.confirm_title") as string}
        onSubmit={handleConfirmSync}
      >
        <div className={styles.modalContent}>
          <div className={styles.modalWarning}>
            <svg
              className={styles.modalWarningIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p>
              <strong>
                {t("settings.elasticsearch.confirm_message") as string}
              </strong>
            </p>
            <ul>
              {Object.values(
                t("settings.elasticsearch.confirm_items", {
                  returnObjects: true,
                }) as Record<string, string>,
              ).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className={styles.modalNote}>
              <strong>
                {t("settings.elasticsearch.confirm_note") as string}
              </strong>
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Settings;
